// [목적] Budget 섹션 완성 데이터를 받아 총액·인당 금액·일별 금액·카테고리 분류·이동 요약 미리보기 카드를 렌더링
// [이유] W7-4 — usePlanGenerateStream completedSections 중 sectionId='budget' 데이터를
//         LIVE PREVIEW 패널의 마지막 섹션으로 progressive append.
//         예산 정보를 숫자와 비율 바로 시각화하여 "내 여행 비용 구조" 즉각 인지 제공.
//         ADR-FGQ-OQ5: perPerson = totalEstimated / headcount FE 계산 (headcount>0 존재 시).
//         ADR-FGQ-OQ6: 1만원 반올림·0원 카테고리 숨김. "약 N만원" / "인당 약 N만원" 형식.
//         WC-CTX-14 (2026-05-31): transitSummary 있으면 "이동" 요약 칩/라인 렌더.
//           transitSummary null/없으면 미표시 — graceful, 기존 레이아웃 무변경.
//           모드 아이콘(🚗/🚌/🚄) + 한국어 라벨 + 왕복 금액(천단위 콤마) + "왕복" 명시.
//           originName → destination 맥락("서울→속초 왕복") 표시.
// [사이드 이팩트] budgetSchema.currency 는 'KRW' 고정 — 다국어 지원 시 통화 유니온 확장 필요.
//                 perDay 배열이 비어있으면 일별 바 미표시.
//                 headcount prop 신규 추가 — 호출 측(PreviewMiniCard/ChatPage)에서 headcount 주입 필요.
//                 transitSummary prop 신규 추가 — 호출 측에서 Budget.transitSummary 를 전달해야 이동 요약 렌더.
// [수정 시 고려사항] Budget 타입은 packages/types plan-stream.ts budgetSchema 파생.
//                   금액 포맷은 formatKrw 헬퍼(1만원 반올림) 사용 — formatKrw 변경 시 전체 표기 갱신.
//                   색상은 카테고리 구분에 brand/accent/warm/paper 토큰을 재사용 — 색만으로 구분 금지(텍스트 병기).
//                   WCAG 2.1 AA — 진행 바에 aria-valuenow/valuemin/valuemax + aria-label 필수.
//                   transitSummary 계약 변경 금지 — plan-stream.ts transitSummarySchema 정본(WC-CTX-08). FE 는 표시만.

import { Box, Typography } from '@mui/material';
import { keyframes, type SxProps, type Theme } from '@mui/material/styles';
import { tokens } from '@gangwoncanvas/ui';
import type { Budget, TransitSummary } from '@gangwoncanvas/types';

const c = tokens.color;
const R = tokens.radius;
const SP = tokens.spacing;
const MO = tokens.motion;
const TY = tokens.typography;
const L = 'light' as const;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const SLIDE_SX: SxProps<Theme> = {
  animation: `${slideIn} ${MO.slow.duration}ms ${MO.slow.easing}`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

// ── 내부 유틸 ────────────────────────────────────────────────────────────────

/**
 * [목적] KRW 금액을 "약 N만원" 형식으로 포맷 — 1만원 반올림 (ADR-FGQ-OQ6)
 * [이유] ADR-FGQ5: "300k" 등 비한국어 단위 금지. 1만원 반올림이 "대략 금액" 의도에 부합.
 *         Math.round(amount/10000) → 5천원 경계에서 반올림(ADR-FGQ-OQ6 명세와 정합).
 * [사이드 이팩트] 반환값이 "약 N만원" 형식으로 변경 — 호출 측 aria-label 문자열도 이 포맷 사용.
 * [수정 시 고려사항] 1만원 미만은 "약 0만원" → 0원 카테고리 필터링으로 이 케이스가 노출되지 않아야 함.
 *                   FolioPage/FolioMobile 의 formatKrwViewer(T15 공유 규칙)와 동일 로직 유지 필수.
 */
export function formatKrw(amount: number): string {
  const man = Math.round(amount / 10000);
  return `약 ${man.toLocaleString('ko-KR')}만원`;
}

/**
 * [목적] 카테고리 breakdown 비율을 계산 (전체 합 대비 %)
 * [이유] 진행 바 너비 결정에 사용. 합계가 0 이면 균등 분배 fallback
 * [사이드 이팩트] 없음 (순수 함수)
 * [수정 시 고려사항] totalEstimated 대신 breakdown 합계 기준으로 계산 — LLM 편차 흡수
 */
function calcPercent(value: number, total: number): number {
  if (total === 0) return 0;
  return Math.min(100, Math.round((value / total) * 100));
}

// ── 카테고리 메타 ─────────────────────────────────────────────────────────────

/**
 * [목적] breakdown 카테고리별 표시 레이블 + 색상 토큰 매핑
 * [이유] 색만으로 정보 전달 금지 — label(텍스트)과 color(bar)를 함께 사용 (WCAG 1.4.1)
 * [사이드 이팩트] budgetSchema.breakdown 키 변경 시 함께 갱신 필요
 * [수정 시 고려사항] color 는 토큰값 직접 사용 (hex 하드코딩 아님)
 */
const CATEGORY_META: Record<keyof Budget['breakdown'], { label: string; color: string }> = {
  accommodation: { label: '숙박', color: c.brand[500][L] },
  food: { label: '식비', color: c.accent[500][L] },
  transport: { label: '교통', color: c.warm[500][L] },
  activity: { label: '액티비티', color: c.ink.mid[L] },
  etc: { label: '기타', color: c.ink.low[L] },
};

// ── 이동 요약 메타 (WC-CTX-14) ───────────────────────────────────────────────

/**
 * [목적] transitSummary.mode → 아이콘 이모지 + 한국어 라벨 매핑 (WC-CTX-14)
 * [이유] 이동 요약 칩/라인 렌더에서 모드별 시각 구분(아이콘) + 텍스트 라벨(색만으로 정보 전달 금지 WCAG 1.4.1) 필요.
 *         slotLabels.ts TRANSPORT_LABEL(슬롯 수집 레벨)과 달리 여기서는 이동비 산출 모드 레벨 표시.
 *         이동비 매핑 규칙(Spec Tech Review): self_drive=자가용🚗 / bus·public·etc=버스🚌 / ktx=KTX🚄.
 * [사이드 이팩트] FolioBudgetPreview 내부에서만 사용 — 외부 export 없음.
 * [수정 시 고려사항] transitSummarySchema.mode 5값(self_drive/public/bus/ktx/etc)과 1:1 대응 유지.
 *                   이모지는 aria-hidden 처리(텍스트 라벨이 의미 전달) — WCAG §1.4.1 색/아이콘 단독 전달 금지.
 *                   'public'·'etc' 는 버스 아이콘으로 폴백 — Spec 이동비 매핑 규칙(대중교통=버스 공식).
 */
const TRANSIT_MODE_META: Record<string, { icon: string; label: string }> = {
  self_drive: { icon: '🚗', label: '자가용' },
  public: { icon: '🚌', label: '버스' }, // 레거시 대중교통 → 버스 공식 폴백
  bus: { icon: '🚌', label: '버스' },
  ktx: { icon: '🚄', label: 'KTX' },
  etc: { icon: '🚌', label: '기타' }, // 기타 → 버스 공식 폴백
};

/**
 * [목적] 이동비 금액을 "N,NNN원" 천단위 콤마 형식으로 포맷
 * [이유] Spec §4-C 4항: 이동비는 amountKrw 천단위 콤마 + "원" 표시.
 *         formatKrw 의 "약 N만원" 반올림 형식과 달리 이동비는 정확한 추정값을 원 단위로 표시(휴리스틱 산출물).
 * [사이드 이팩트] 없음 (순수 함수, FolioBudgetPreview 내부 전용)
 * [수정 시 고려사항] amountKrw=0 이면 "0원" — transitSummarySchema min(0) 허용값. 0원 이동비도 렌더함(graceful).
 *                   금액 포맷 변경 시 테스트 단언 TC-TS-* 도 동기 갱신 필요.
 */
function formatTransitAmount(amountKrw: number): string {
  return `${amountKrw.toLocaleString('ko-KR')}원`;
}

// ── props / 컴포넌트 ─────────────────────────────────────────────────────────

export interface FolioBudgetPreviewProps {
  data: Budget;
  /**
   * ADR-FGQ-OQ5: perPerson = totalEstimated / headcount FE 계산.
   * headcount>0 이면 "인당 약 N만원" 표시, 0/undefined 이면 총액만 표시.
   * 출처: slotsSchema.headcount (chat confirm 시 전달) 또는 companions 인원 파생.
   */
  headcount?: number | null;
  /**
   * WC-CTX-14: 이동 요약 메타 — transitSummary 있으면 "이동" 칩/라인 렌더, null/없으면 미표시(graceful).
   * 출처: Budget.transitSummary (budgetSchema 내 optional 필드) 또는 folioSchema.transitSummary.
   * 호출 측에서 data.transitSummary 를 분리하여 전달하거나 data 에서 직접 파생해도 무방.
   */
  transitSummary?: TransitSummary | null;
  sx?: SxProps<Theme>;
}

/**
 * [목적] Budget 섹션 미리보기 카드 — 총액·인당·일별 금액 바·카테고리 breakdown·이동 요약
 * [이유] 폴리오 생성 마지막 섹션으로, "전체 여행 비용 구조"를 시각화하여 결과 완성감 제공.
 *         ADR-FGQ-OQ5: headcount 있으면 인당 금액(FE 계산) 추가 표시.
 *         ADR-FGQ-OQ6: 1만원 반올림·0원 카테고리 숨김·"약 N만원" / "인당 약 N만원" 표기.
 *         WC-CTX-14: transitSummary 있으면 "이동" 요약 칩/라인(모드 아이콘+라벨, 금액, 왕복, 출발→도착 맥락).
 *           transitSummary null/없으면 미표시 — 기존 레이아웃 무변경(graceful).
 * [사이드 이팩트] formatKrw 포맷 변경(1만원 반올림, "약 N만원")으로 일별 바·breakdown 금액 텍스트도 변경됨.
 *                 transitSummary prop 추가로 호출 측에서 Budget.transitSummary 또는 folioSchema.transitSummary 전달 필요.
 * [수정 시 고려사항] perDay dayIndex 는 1-based — idx+1 과 일치 확인.
 *                   breakdown 합계 > totalEstimated 인 경우 비율 바가 100% 초과 — calcPercent 에서 min(100) 처리됨.
 *                   0원 카테고리 숨김: Math.round(amt/10000)===0 인 항목 제외(ADR-FGQ-OQ6).
 *                   이동 요약 섹션은 breakdown '교통' 행과 별개 — 두 정보 중복 표시는 의도적(맥락 정보 보강).
 *                   transitSummary 계약 변경 금지 — plan-stream.ts transitSummarySchema 정본(WC-CTX-08).
 */
export function FolioBudgetPreview({
  data,
  headcount,
  transitSummary,
  sx,
}: FolioBudgetPreviewProps): JSX.Element {
  const { totalEstimated, perDay, breakdown } = data;

  // [목적] 0원(반올림 후 0만원) 카테고리 숨김 (ADR-FGQ-OQ6)
  // [이유] 1만원 미만 카테고리는 "약 0만원"으로 표시되어 시각적으로 무의미 — 숨김 처리
  // [사이드 이팩트] breakdownTotal, calcPercent 분모 모두 필터링 후 값 기준으로 계산
  // [수정 시 고려사항] 필터 기준은 Math.round(amt/10000)===0 (1만원 반올림 기준)
  const visibleBreakdown = (
    Object.entries(breakdown) as [keyof Budget['breakdown'], number][]
  ).filter(([, amt]) => Math.round(amt / 10000) > 0);

  const breakdownTotal = visibleBreakdown.reduce((s, [, v]) => s + v, 0);

  // [목적] 인당 금액 FE 계산 (ADR-FGQ-OQ5)
  // [이유] headcount 슬롯 이미 존재, FE 계산이 일관·검증 용이. LLM perPerson 미사용.
  // [사이드 이팩트] headcount 미지정(null/0/undefined) 시 perPersonLabel=null → 총액만 표시
  // [수정 시 고려사항] headcount 소수 방지: Math.floor 아닌 headcount 자체가 int(slotsSchema min(1))
  const perPersonMan =
    headcount && headcount > 0 ? Math.round(totalEstimated / headcount / 10000) : null;

  return (
    <Box
      sx={
        [
          SLIDE_SX,
          {
            bgcolor: c.paper[0][L],
            border: `1px solid ${c.line.base[L]}`,
            borderRadius: `${R.l}px`,
            overflow: 'hidden',
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ] as SxProps<Theme>
      }
    >
      {/* 헤더 */}
      <Box
        sx={{
          px: `${SP[3]}px`,
          py: `${SP[2]}px`,
          borderBottom: `1px solid ${c.line.soft[L]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: `${SP[2]}px` }}>
          <Box component="span" aria-hidden="true" sx={{ fontSize: 14, lineHeight: 1 }}>
            💰
          </Box>
          <Typography
            sx={{
              fontSize: 9.5,
              fontWeight: 700,
              color: c.ink.mid[L],
              fontFamily: TY.fontFamily.mono,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}
          >
            BUDGET
          </Typography>
        </Box>

        {/* 총액 + 인당 금액 (ADR-FGQ-OQ5) */}
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: c.ink.high[L],
              fontFamily: TY.fontFamily.mono,
            }}
            aria-label={`총 예산 ${formatKrw(totalEstimated)}`}
          >
            {formatKrw(totalEstimated)}
          </Typography>
          {perPersonMan !== null && (
            <Typography
              sx={{
                fontSize: 9.5,
                color: c.ink.low[L],
                fontFamily: TY.fontFamily.mono,
                mt: '1px',
              }}
              aria-label={`인당 약 ${perPersonMan.toLocaleString('ko-KR')}만원`}
            >
              인당 약 {perPersonMan.toLocaleString('ko-KR')}만원
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ p: `${SP[3]}px` }}>
        {/* 일별 금액 바 */}
        {perDay.length > 0 && (
          <Box sx={{ mb: `${SP[3]}px` }}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: c.ink.low[L],
                fontFamily: TY.fontFamily.mono,
                letterSpacing: '0.4px',
                mb: `${SP[1]}px`,
                textTransform: 'uppercase',
              }}
            >
              일별 예산
            </Typography>

            <Box
              component="ul"
              aria-label="일별 예산 목록"
              sx={{
                m: 0,
                p: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
            >
              {perDay.map(({ dayIndex, amount }) => {
                const maxAmount = Math.max(...perDay.map((d) => d.amount), 1);
                const pct = calcPercent(amount, maxAmount);

                return (
                  <Box
                    key={dayIndex}
                    component="li"
                    sx={{ display: 'flex', alignItems: 'center', gap: `${SP[2]}px` }}
                  >
                    {/* Day 라벨 */}
                    <Typography
                      sx={{
                        fontSize: 9.5,
                        color: c.ink.mid[L],
                        fontFamily: TY.fontFamily.mono,
                        fontWeight: 600,
                        width: 24,
                        flexShrink: 0,
                      }}
                      aria-hidden="true"
                    >
                      {`D${dayIndex}`}
                    </Typography>

                    {/* 진행 바 */}
                    <Box
                      role="progressbar"
                      aria-valuenow={pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`Day ${dayIndex} 예산 ${formatKrw(amount)}`}
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: `${R.s}px`,
                        bgcolor: c.paper[2][L],
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${pct}%`,
                          height: '100%',
                          bgcolor: c.brand[500][L],
                          borderRadius: 'inherit',
                          transition: `width ${MO.med.duration}ms ${MO.med.easing}`,
                        }}
                      />
                    </Box>

                    {/* 금액 텍스트 — 바만으로 정보 전달 금지 보완 */}
                    <Typography
                      sx={{
                        fontSize: 9.5,
                        color: c.ink.mid[L],
                        fontFamily: TY.fontFamily.mono,
                        width: 40,
                        textAlign: 'right',
                        flexShrink: 0,
                      }}
                      aria-hidden="true"
                    >
                      {formatKrw(amount)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* 카테고리 breakdown */}
        <Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              color: c.ink.low[L],
              fontFamily: TY.fontFamily.mono,
              letterSpacing: '0.4px',
              mb: `${SP[1]}px`,
              textTransform: 'uppercase',
            }}
          >
            항목별
          </Typography>

          <Box
            component="ul"
            aria-label="항목별 예산 목록"
            sx={{
              m: 0,
              p: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            {/* ADR-FGQ-OQ6: visibleBreakdown — 0원(1만원 반올림 후 0) 카테고리 이미 필터링됨 */}
            {visibleBreakdown.map(([key, amount]) => {
              const meta = CATEGORY_META[key];
              const pct = calcPercent(amount, breakdownTotal);

              return (
                <Box
                  key={key}
                  component="li"
                  sx={{ display: 'flex', alignItems: 'center', gap: `${SP[2]}px` }}
                >
                  {/* 색상 도트 + 텍스트 라벨 병기 — 색만으로 구분 금지(WCAG 1.4.1) */}
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: meta.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: c.ink.mid[L],
                      width: 52,
                      flexShrink: 0,
                    }}
                  >
                    {meta.label}
                  </Typography>

                  {/* 비율 바 */}
                  <Box
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${meta.label} ${formatKrw(amount)} (${pct}%)`}
                    sx={{
                      flex: 1,
                      height: 5,
                      borderRadius: `${R.s}px`,
                      bgcolor: c.paper[2][L],
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${pct}%`,
                        height: '100%',
                        bgcolor: meta.color,
                        borderRadius: 'inherit',
                        transition: `width ${MO.med.duration}ms ${MO.med.easing}`,
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 9.5,
                      color: c.ink.mid[L],
                      fontFamily: TY.fontFamily.mono,
                      width: 48,
                      textAlign: 'right',
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  >
                    {formatKrw(amount)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── WC-CTX-14: 이동 요약 칩/라인 ────────────────────────────────────
            [목적] transitSummary 있으면 "이동" 요약(모드 아이콘+라벨·금액·왕복·출발→도착) 렌더.
            [이유] Spec §4-C 4항: 폴리오에 이동비 이중 노출(breakdown 합산 + 요약 칩).
                   "서울→속초 왕복 약 N원" 형식으로 여행 의사결정 핵심 정보 즉각 인지.
            [사이드 이팩트] transitSummary null/없으면 미표시 — 기존 레이아웃 무변경(graceful).
            [수정 시 고려사항] 디자인 토큰만 사용(#hex·px 하드코딩 금지 CLAUDE §6-3).
                              aria-label 에 모드·금액·왕복 맥락 포함(WCAG 2.1 AA).
                              "대략/왕복" 명시 — Spec §8 이동비 정확도 기대치 리스크 완화.
            ────────────────────────────────────────────────────────────────── */}
        {transitSummary != null && (
          <Box
            sx={{
              mt: `${SP[2]}px`,
              pt: `${SP[2]}px`,
              borderTop: `1px solid ${c.line.soft[L]}`,
            }}
          >
            {/* 섹션 헤더 */}
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: c.ink.low[L],
                fontFamily: TY.fontFamily.mono,
                letterSpacing: '0.4px',
                mb: `${SP[1]}px`,
                textTransform: 'uppercase',
              }}
            >
              이동
            </Typography>

            {/* 이동 요약 칩 행 */}
            <Box
              role="region"
              aria-label={`이동 요약: ${TRANSIT_MODE_META[transitSummary.mode]?.label ?? transitSummary.mode} ${formatTransitAmount(transitSummary.amountKrw)} 왕복`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: `${SP[1]}px`,
                bgcolor: c.paper[2][L],
                borderRadius: `${R.m}px`,
                px: `${SP[3]}px`,
                py: `${SP[2]}px`,
              }}
            >
              {/* 모드 아이콘 + 라벨 — 색만으로 구분 금지(아이콘+텍스트 병기 WCAG 1.4.1) */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: `${SP[1]}px`,
                  flexShrink: 0,
                }}
              >
                <Box component="span" aria-hidden="true" sx={{ fontSize: 14, lineHeight: 1 }}>
                  {TRANSIT_MODE_META[transitSummary.mode]?.icon ?? '🚌'}
                </Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: c.ink.high[L],
                  }}
                >
                  {TRANSIT_MODE_META[transitSummary.mode]?.label ?? transitSummary.mode}
                </Typography>
              </Box>

              {/* 구분자 */}
              <Typography
                aria-hidden="true"
                sx={{ fontSize: 10, color: c.ink.low[L], flexShrink: 0 }}
              >
                ·
              </Typography>

              {/* 출발지→목적지 맥락 + 왕복 명시 */}
              <Typography
                sx={{
                  fontSize: 10.5,
                  color: c.ink.mid[L],
                  flexShrink: 0,
                }}
              >
                {transitSummary.originName} 출발 · 왕복
              </Typography>

              {/* 구분자 */}
              <Typography
                aria-hidden="true"
                sx={{ fontSize: 10, color: c.ink.low[L], flexShrink: 0 }}
              >
                ·
              </Typography>

              {/* 금액 — "대략" 명시로 휴리스틱 오차 투명성 확보(Spec §8) */}
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: c.warm[500][L],
                  fontFamily: TY.fontFamily.mono,
                  flexShrink: 0,
                }}
                aria-label={`왕복 이동비 대략 ${formatTransitAmount(transitSummary.amountKrw)}`}
              >
                대략 {formatTransitAmount(transitSummary.amountKrw)}
              </Typography>
            </Box>

            {/* 편도 거리 참고 (oneWayKm > 0 인 경우만 표시 — 선택적 맥락 정보) */}
            {transitSummary.oneWayKm > 0 && (
              <Typography
                sx={{
                  fontSize: 9.5,
                  color: c.ink.low[L],
                  fontFamily: TY.fontFamily.mono,
                  mt: `${SP[1]}px`,
                  pl: `${SP[1]}px`,
                }}
                aria-label={`편도 약 ${Math.round(transitSummary.oneWayKm)}km 기준`}
              >
                편도 약 {Math.round(transitSummary.oneWayKm)}km 기준 · 실제 운임과 차이 있을 수 있음
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
